import React from 'react';
import {
  AnimatePresence,
  LayoutGroup,
  motion,
  useReducedMotion,
} from 'framer-motion';

import { ClockIcon, CoffeeIcon } from '../../../assets/svg/SVGComponents';
import { IBoardSlot } from '../../../dtos/IBoardSnapshot';
import { formatTime, modalityLabel } from '../logic/labels';
import { splitSchedule } from '../logic/schedule';
import {
  colors,
  DelayNote,
  IconText,
  LateTag,
  LiveDot,
  Panel,
  SlotRow,
  SlotTime,
} from '../styles';

interface ISchedulePanelProps {
  upcoming: IBoardSlot[];
  /** Relógio em hora de parede (UTC), a escala do cronograma. */
  now: number;
  /** Slots que acabaram de começar: entram em destaque (FE-15). */
  started: string[];
}

const MotionSlotRow = motion.create(SlotRow);

interface ISlotLineProps {
  slot: IBoardSlot;
  highlight?: boolean;
  /** Horário passou e a rodada não foi aberta (FE-76). */
  lateMinutes?: number;
}

// forwardRef: o AnimatePresence em "popLayout" mede o item que sai.
const SlotLine = React.forwardRef<HTMLDivElement, ISlotLineProps>(
  ({ slot, highlight, lateMinutes }, ref) => {
    const reduce = useReducedMotion();

    return (
      <MotionSlotRow
        ref={ref}
        // Mesmo layoutId nas duas listas: o slot "voa" de Próximas para Agora.
        layoutId={slot.slot_id}
        layout="position"
        $ongoing={slot.status === 'ongoing'}
        initial={reduce ? { opacity: 0 } : { opacity: 0, x: 40 }}
        animate={
          highlight && !reduce
            ? {
                opacity: 1,
                x: 0,
                boxShadow: [
                  `inset 4px 0 0 ${colors.live}, 0 0 0 0 rgba(255,152,0,0.8)`,
                  `inset 4px 0 0 ${colors.live}, 0 0 0 0.9rem rgba(255,152,0,0)`,
                ],
              }
            : { opacity: 1, x: 0 }
        }
        exit={reduce ? { opacity: 0 } : { opacity: 0, x: -40 }}
        transition={{
          type: 'spring',
          bounce: 0.2,
          duration: 0.6,
          boxShadow: { duration: 1.2, repeat: 3, ease: 'easeOut' },
        }}
      >
        <SlotTime>{formatTime(slot.starts_at)}</SlotTime>
        {slot.is_break ? (
          <strong>
            <IconText>
              <CoffeeIcon />
              {slot.break_label || 'Intervalo'}
            </IconText>
          </strong>
        ) : (
          <>
            <strong>
              {slot.team_name}
              {lateMinutes !== undefined && (
                <LateTag aria-label={`Atrasada ${lateMinutes} minutos`}>
                  Atrasada
                </LateTag>
              )}
              {highlight && (
                <motion.span
                  style={{
                    display: 'inline-block',
                    marginLeft: '0.5em',
                    padding: '0.05em 0.5em',
                    borderRadius: 999,
                    background: colors.live,
                    color: '#fff',
                    fontSize: '0.6em',
                    letterSpacing: '0.08em',
                    verticalAlign: 'middle',
                  }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', bounce: 0.5, delay: 0.3 }}
                >
                  COMEÇOU!
                </motion.span>
              )}
            </strong>
            <small>
              {[slot.arena_name, modalityLabel(slot.modality)]
                .filter(Boolean)
                .map((part, index) => (
                  <React.Fragment key={part}>
                    {index > 0 && ' · '}
                    <span
                      style={{
                        whiteSpace:
                          part === slot.arena_name ? 'nowrap' : undefined,
                        fontFamily:
                          part === slot.arena_name
                            ? 'RedHatDisplay-Bold'
                            : undefined,
                      }}
                    >
                      {part}
                    </span>
                  </React.Fragment>
                ))}
            </small>
          </>
        )}
      </MotionSlotRow>
    );
  },
);

SlotLine.displayName = 'SlotLine';

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SlotRow>
    <span />
    <small style={{ gridColumn: 'span 2' }}>{children}</small>
  </SlotRow>
);

/** Lateral "Agora / Próximas", filtrada pelo relógio da tela (FE-04). */
const SchedulePanel: React.FC<ISchedulePanelProps> = ({
  upcoming,
  now,
  started,
}) => {
  const { ongoing, currentBreak, next, late, maxDelay } = splitSchedule(
    upcoming,
    now,
  );

  return (
    <LayoutGroup>
      <Panel>
        <h2>
          <LiveDot />
          Agora
        </h2>
        <AnimatePresence initial={false} mode="popLayout">
          {currentBreak && (
            <SlotLine key={currentBreak.slot_id} slot={currentBreak} />
          )}
          {ongoing.map(slot => (
            <SlotLine
              key={slot.slot_id}
              slot={slot}
              highlight={started.includes(slot.slot_id)}
            />
          ))}
        </AnimatePresence>
        {!currentBreak && ongoing.length === 0 && (
          <Empty>Nenhuma rodada em andamento.</Empty>
        )}
      </Panel>

      <Panel>
        <h2>
          <IconText>
            {/* Clock é 16×16: stroke 1 equivale ao 1.5 dos ícones de 24. */}
            <ClockIcon strokeWidth={1} />
            Próximas
          </IconText>
          {maxDelay && (
            <DelayNote>
              {maxDelay.arena_name ? `${maxDelay.arena_name} · ` : ''}
              {maxDelay.minutes} min de atraso
            </DelayNote>
          )}
        </h2>
        <AnimatePresence initial={false} mode="popLayout">
          {next.map(slot => (
            <SlotLine
              key={slot.slot_id}
              slot={slot}
              lateMinutes={late[slot.slot_id]}
            />
          ))}
        </AnimatePresence>
        {next.length === 0 && <Empty>Sem rodadas programadas.</Empty>}
      </Panel>
    </LayoutGroup>
  );
};

export default SchedulePanel;
